// apps/web/src/components/ImportButton.tsx
import { useRef, useState } from 'react';
import { Upload, RotateCcw, AlertCircle, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Button } from '@hump/ui';
import { useLibraryStore } from '../stores/library';

/** 失败的导入项（保留用于重试） */
interface FailedImport {
  filename: string;
  content: ArrayBuffer;
  error: string;
}

export function ImportButton() {
  const inputRef = useRef<HTMLInputElement>(null);
  const importFile = useLibraryStore((s) => s.importFile);
  const [importing, setImporting] = useState(false);
  const [failed, setFailed] = useState<FailedImport[]>([]);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  // 1.8 导入进度：当前/总数
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setImporting(true);
    const fileList = Array.from(files);
    setProgress({ done: 0, total: fileList.length });
    const newFailed: FailedImport[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const content = await file.arrayBuffer();
      // 复制一份 ArrayBuffer（原始 buffer 可能被 transfer 后不可用）
      const copy = content.slice(0);
      try {
        await importFile(file.name, copy);
      } catch (e) {
        newFailed.push({ filename: file.name, content: content.slice(0), error: (e as Error).message });
      }
      setProgress({ done: i + 1, total: fileList.length });
    }

    setFailed(newFailed);
    setImporting(false);
    setProgress(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function retryOne(item: FailedImport) {
    setImporting(true);
    setProgress({ done: 0, total: 1 });
    try {
      await importFile(item.filename, item.content.slice(0));
      // 成功 → 从失败列表移除
      setFailed((prev) => prev.filter((f) => f !== item));
    } catch {
      // 仍失败，保留在列表（error 已在 store 中）
    }
    setProgress({ done: 1, total: 1 });
    setImporting(false);
    setProgress(null);
  }

  // 对齐 features.md 1.5：URL 网页导入
  // 输入 URL 后调用 docRepository.importFile(url, url) 触发 UrlParser 解析
  async function handleUrlImport() {
    const url = urlValue.trim();
    setUrlError(null);
    if (!url) {
      setUrlError('请输入 URL');
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      setUrlError('请输入完整的 URL（以 http:// 或 https:// 开头）');
      return;
    }
    setImporting(true);
    setProgress({ done: 0, total: 1 });
    try {
      // filename 与 content 都传 URL，触发 UrlParser 解析
      await importFile(url, url);
      // 成功 → 关闭输入框并清空
      setShowUrlInput(false);
      setUrlValue('');
    } catch (e) {
      // 错误已在 store 中，这里展示给用户
      setUrlError((e as Error).message);
    }
    setProgress({ done: 1, total: 1 });
    setImporting(false);
    setProgress(null);
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <input
        ref={inputRef}
        type="file"
        accept=".md,.markdown,.pdf,.epub,.docx"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="flex items-center gap-2">
        {/* URL 导入入口（对齐 features.md 1.5） */}
        <button
          type="button"
          onClick={() => setShowUrlInput((v) => !v)}
          disabled={importing}
          title="从 URL 导入网页"
          aria-label="从 URL 导入网页"
          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border-warm dark:border-border-warm-dark text-charcoal dark:text-charcoal-light hover:bg-warm-white dark:hover:bg-warm-dark disabled:opacity-50 transition-colors"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        <Button
          variant="primary"
          size="md"
          disabled={importing}
          onClick={() => inputRef.current?.click()}
        >
          {importing ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-1" />
          )}
          {importing ? '导入中...' : '导入'}
        </Button>
      </div>

      {/* 1.8 导入进度 */}
      {importing && progress && (
        <div className="flex items-center gap-2 text-xs text-charcoal/60 dark:text-charcoal-light/60 w-full max-w-sm justify-end">
          <Loader2 className="w-3 h-3 animate-spin shrink-0" />
          <span>
            {progress.total > 1
              ? `正在导入 ${Math.min(progress.done + 1, progress.total)}/${progress.total} 文件...`
              : '正在解析...'}
          </span>
        </div>
      )}

      {/* URL 输入弹层 */}
      {showUrlInput && (
        <div className="flex flex-col gap-1 w-full max-w-sm px-3 py-2 rounded-md bg-warm-white dark:bg-warm-dark border border-border-warm dark:border-border-warm-dark">
          <label className="text-xs text-charcoal/70 dark:text-charcoal-light/70">
            网页 URL
          </label>
          <div className="flex items-center gap-1">
            <input
              type="url"
              value={urlValue}
              onChange={(e) => {
                setUrlValue(e.target.value);
                setUrlError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleUrlImport();
                }
              }}
              placeholder="https://example.com/article"
              disabled={importing}
              autoFocus
              className="flex-1 min-w-0 px-2 py-1.5 rounded-md text-sm bg-white dark:bg-warm-dark border border-border-warm dark:border-border-warm-dark text-charcoal dark:text-charcoal-light focus:outline-none focus:ring-1 focus:ring-oasis disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleUrlImport}
              disabled={importing}
              className="shrink-0 px-2.5 py-1.5 rounded-md text-sm font-medium bg-camel text-white hover:bg-camel-light disabled:opacity-50 transition-colors"
            >
              {importing ? '...' : '导入'}
            </button>
          </div>
          {urlError && (
            <p className="text-xs text-error flex items-center gap-1 mt-0.5">
              <AlertCircle className="w-3 h-3 shrink-0" />
              <span className="truncate">{urlError}</span>
            </p>
          )}
        </div>
      )}

      {/* 失败项列表 + 重试 */}
      {failed.length > 0 && (
        <div className="flex flex-col gap-1 w-full max-w-sm">
          {failed.map((item, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-md bg-error/10 border border-error/20">
              <AlertCircle className="w-4 h-4 text-error shrink-0" />
              <span className="text-xs text-charcoal dark:text-charcoal-light truncate flex-1">
                {item.filename}
              </span>
              <button
                onClick={() => retryOne(item)}
                disabled={importing}
                className="flex items-center gap-1 text-xs text-camel hover:text-camel-dark disabled:opacity-50 shrink-0"
              >
                <RotateCcw className="w-3 h-3" />
                重试
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
